import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from 'axios'
import { TOKEN_TYPE, getTokenFromCookies, setTokenToCookies, setTokenExpiry, clearAllTokens } from './cookies'
import { getRootDomain } from './domain'
import { redirectToSSOLogin } from './sso'
import { SSO_API_BASE_URL, AUTH_ENDPOINTS } from '@/features/auth/api/endpoints'

const SSO_REFRESH_URL = `${SSO_API_BASE_URL}${AUTH_ENDPOINTS.refresh}`

function getCookieDomain(): string | undefined {
  const root = getRootDomain(window.location.hostname)
  return root ? `.${root}` : undefined
}

interface RefreshResult {
  token: string | null
  errorType: 'unauthorized' | 'network' | 'none'
}

// In-flight refresh promise — đảm bảo nhiều requests 401 đồng thời chỉ trigger 1 refresh
let refreshPromise: Promise<RefreshResult> | null = null

async function performRefresh(): Promise<RefreshResult> {
  const refreshToken = getTokenFromCookies(TOKEN_TYPE.REFRESH_TOKEN)
  if (!refreshToken) return { token: null, errorType: 'unauthorized' }

  try {
    const res = await axios.post(SSO_REFRESH_URL, { refresh_token: refreshToken }, {
      headers: { 'Content-Type': 'application/json' },
      timeout: 10000,
    })
    const data = res.data
    if (!data?.success || !data?.data?.access_token) {
      return { token: null, errorType: 'unauthorized' }
    }

    const { access_token, refresh_token, expires_in } = data.data
    const domain = getCookieDomain()
    setTokenToCookies(TOKEN_TYPE.ACCESS_TOKEN, access_token, expires_in, domain)
    setTokenToCookies(TOKEN_TYPE.REFRESH_TOKEN, refresh_token, 7 * 24 * 60 * 60, domain)
    setTokenExpiry(new Date(Date.now() + expires_in * 1000).toISOString(), domain)
    return { token: access_token, errorType: 'none' }
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const status = error.response.status
      if (status === 400 || status === 401 || status === 403) {
        return { token: null, errorType: 'unauthorized' }
      }
    }
    return { token: null, errorType: 'network' }
  }
}

function getRefreshPromise(): Promise<RefreshResult> {
  if (!refreshPromise) {
    refreshPromise = performRefresh().finally(() => {
      refreshPromise = null
    })
  }
  return refreshPromise
}

/**
 * Cài interceptor chung cho mọi axios instance:
 * - Request: gắn Bearer token
 * - Response 401: thử refresh 1 lần (queue tránh refresh đồng thời), retry request
 * - Refresh thất bại: chỉ clear tokens + redirect SSO nếu lỗi thực sự là unauthorized
 */
export function setupAuthInterceptor(instance: AxiosInstance): void {
  instance.interceptors.request.use((config) => {
    const token = getTokenFromCookies(TOKEN_TYPE.ACCESS_TOKEN)
    if (token) config.headers.Authorization = `Bearer ${token}`
    return config
  })

  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }
      const status = error.response?.status

      // Skip nếu không phải 401 hoặc đã retry rồi
      if (status !== 401 || !originalRequest || originalRequest._retry) {
        return error.response || Promise.reject(error)
      }

      // Skip nếu chính request là refresh (tránh recursive)
      if (originalRequest.url?.includes('/auth/refresh')) {
        clearAllTokens(getCookieDomain())
        return error.response || Promise.reject(error)
      }

      originalRequest._retry = true
      const refreshResult = await getRefreshPromise()

      if (refreshResult.token) {
        originalRequest.headers.Authorization = `Bearer ${refreshResult.token}`
        return instance(originalRequest)
      }

      // Chỉ clear tokens và redirect SSO nếu refresh token thực sự hết hạn / không hợp lệ
      if (refreshResult.errorType === 'unauthorized') {
        clearAllTokens(getCookieDomain())
        redirectToSSOLogin()
      } else {
        console.error('Refresh token request failed due to network/server error. Session retained.')
      }

      return error.response || Promise.reject(error)
    },
  )
}
